import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ClientService } from '../client.service';
import { UserService } from '../user.service';
import { ToolDef, ToolEntry, Registration, ToolContext, ApprovalAsk, ApprovalAnswer, ZavoBridge } from './mcp-types';
import { getZavoBridge } from './zavo-bridge';

/**
 * zone.js replaces the page's Promise, and the preload's bridge carries only native promises across
 * intact: a zone promise keeps then() on its prototype, which the bridge drops, so main would get an
 * empty object for every answer. zone.js keeps the native Promise aside; answers go back on that.
 */
export function nativePromise(win: any = typeof window !== 'undefined' ? window : globalThis): PromiseConstructor {
  const key = win.Zone?.__symbol__?.('Promise');
  return (key && win[key]) || win.Promise;
}

/** Build a Registration payload from a map of tools — pure, no Angular DI needed. */
export function buildRegistrationFromTools(
  tools: Map<string, ToolDef>,
  editorBuild: string,
  instructions: string,
): Registration {
  const entries: ToolEntry[] = [];
  for (const t of tools.values()) {
    const entry: ToolEntry = {
      name: t.name,
      kind: t.kind,
      description: t.description,
      inputSchema: t.inputSchema,
    };
    if (t.recordDataPaths?.length) entry.recordDataPaths = t.recordDataPaths;
    entries.push(entry);
  }
  return { tools: entries, instructions, editorBuild };
}

@Injectable({ providedIn: 'root' })
export class ToolRegistryService {
  private router = inject(Router);
  private clientService = inject(ClientService);
  private userService = inject(UserService);

  private tools = new Map<string, ToolDef>();

  add(tool: ToolDef): void {
    this.tools.set(tool.name, tool);
  }

  /** Build the registration payload — tool entries without run functions. */
  buildRegistration(editorBuild: string, instructions: string): Registration {
    return buildRegistrationFromTools(this.tools, editorBuild, instructions);
  }

  /**
   * Register tools with the desktop bridge. Does nothing in a plain browser.
   * Returns the bridge if found, or null.
   */
  register(editorBuild: string, instructions: string): ZavoBridge | null {
    const bridge = getZavoBridge();
    if (!bridge) return null;
    bridge.registerTools(this.buildRegistration(editorBuild, instructions));
    return bridge;
  }

  get(name: string): ToolDef | undefined {
    return this.tools.get(name);
  }

  names(): string[] {
    return [...this.tools.keys()];
  }

  /** Build a ToolContext from the current editor state. */
  async buildContext(
    request: string,
    askApproval: (ask: ApprovalAsk) => Promise<ApprovalAnswer>,
  ): Promise<ToolContext> {
    const projectId = this.clientService.selectedClientId;
    const projectName = this.clientService.selectedClient?.name ?? '';
    const builderRole = await this.userService.getCurrentUserRole();
    return {
      projectId,
      projectName,
      routerUrl: this.router.url,
      builderRole,
      request,
      askApproval,
    };
  }
}
